import React, { useState, useCallback, useMemo } from "react";
import ReactFlow, {
  ReactFlowProvider,
  Handle,
  Position,
  Node,
  MarkerType,
  Edge,
} from "reactflow";
import { IoMdCheckmarkCircle } from "react-icons/io";
import { TbEyeSearch } from "react-icons/tb";
import "reactflow/dist/style.css";
import SubmitButton from "../Button";
import styles from "../../Pages/SelectQuestions/style.module.scss";
import IndiaImg from "../../icons/ph.svg"
import { OverlayTrigger, Tooltip } from "react-bootstrap";

const H_SPACING = 640;
const V_SPACING = 15;
const NODE_WIDTH = 380;
const NODE_HEIGHT = 48;

// ---------- Custom Node ----------
const CollapsibleNode: React.FC<{ data: any }> = ({ data }) => {
  const {
    id,
    themeID,
    label,
    recordId,            // If exists → leaf node (Option 2)
    hasParent,
    hasChildren,
    isExpanded,
    onToggle,
    clauseID,
    topicName,
    description,
    onLeafClick,
    hasQuestions,
    policyName
  } = data;

  // ----------------------------
  // LABEL TRUNCATION (35 chars)
  // ----------------------------
  const displayLabel = useMemo(() => {
    const maxLength = 35;
    const shouldTruncate = (!recordId) || hasParent;
    // recordId = leaf → no need to truncate unless parent forces it

    const text = label ?? "";
    if (shouldTruncate && text.length > maxLength) {
      return text.substring(0, maxLength) + " ...";
    }
    return text;
  }, [label, hasParent, recordId]);

  // ----------------------------------------
  // MAIN CLICK → Option A behavior
  // ----------------------------------------
  const handleNodeClick = (e?: React.MouseEvent) => {
    // CASE 1: Leaf (recordId exists) → trigger leaf click
    if (recordId) {
      onLeafClick?.(id, label, recordId, clauseID, topicName, description, themeID, policyName);
      return;
    }

    // CASE 2: Non-leaf with children → TOGGLE expand/collapse
    if (hasChildren) {
      onToggle?.();
    }
  };

  // Keyboard support (Enter/Space)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleNodeClick();
    }
  };

  return (
    <div
      onClick={handleNodeClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        position: "relative",
        minWidth: NODE_WIDTH,
        minHeight: NODE_HEIGHT,
        padding: "8px 18px",
        borderRadius: 10,
        background: "#0b1a4a",
        color: "white",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        maxWidth: 380,
        maxHeight: 100,
        fontWeight: 600,
        textAlign: "start",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        justifyContent: "space-between",
      }}
      aria-label={label}
    >
      {/* Left handle (connection) */}
      {hasParent && (
        <Handle
          type="target"
          position={Position.Left}
          style={{ opacity: 0, pointerEvents: "none" }}
          isConnectable={false}
        />
      )}

      {/* LABEL WITH CLAUSE ID */}
      <div
        style={{
          paddingLeft: 12,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {clauseID ? `${clauseID}. ${displayLabel}` : displayLabel}
      </div>

      {/* Completed checkmark */}
      {hasQuestions && (
        <IoMdCheckmarkCircle fill="white" size={20} style={{ marginLeft: 8 }} />
      )}

      {/* Child nodes exist → show dot */}
      {hasChildren && (
        <>
          {/* Invisible source handle */}
          <Handle
            type="source"
            position={Position.Right}
            style={{
              opacity: 0,
              width: 12,
              height: 12,
              pointerEvents: "none",
            }}
            isConnectable={false}
          />

          {/* DOT BUTTON — only expands, not collapses */}
          <div
            onClick={(e) => {
              e.stopPropagation();
              onToggle?.(); // toggle both expand + collapse
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                if (!isExpanded) onToggle?.();
              }
            }}
            aria-label={isExpanded ? "Collapse node" : "Expand node"}
            style={{
              position: "absolute",
              right: -11,
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: "#0b1a4a",
              color: "white",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid white",
              cursor: "pointer",
              fontSize: 16,
              boxShadow: "0 2px 8px rgba(94, 88, 88, 0.12)",
              userSelect: "none",
              zIndex: 10,
            }}
          >
            {isExpanded ? "−" : "+"}
          </div>
        </>
      )}
    </div>
  );
};


const nodeTypes = { collapsible: CollapsibleNode };

/* ---------- Build Tree (unique IDs + leaf-only hasQuestions + topicName propagate + skip duplicate theme/policy) ---------- */
const buildTree = (data: any[], questionData: any[] = []) => {
  const traverse = (node: any, path = "", topicName?: string, parentName?: string, depth = 0, parentChain: any[] = []): any => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const isLeaf = !hasChildren;
    const newParentChain = [...parentChain, node];
    if (node.name === parentName && !node.id) {
      return node.children?.map((child: any) =>
        traverse(child, path, topicName, parentName, depth + 1, newParentChain)
      );
    }

    const nodeKey = node.id ? node.id.toString() : node.name;

    const uniqueId = path
      ? `${path}_${nodeKey}_${topicName}_${depth}`
      : `${nodeKey}_${depth}`;
    let currentTopicName = topicName;

    if (depth === 2) {
      // Ideal: set depth-2 parent
      currentTopicName = node.name;
    } else if (!topicName && depth === 1) {
      // If no depth-2 exists, fallback to depth-1
      currentTopicName = node.name;
    } else if (!topicName && depth === 0) {
      // Last fallback: use root
      currentTopicName = node.name;
    }

    const isMatched =
      // isLeaf &&
      questionData.some((q) => q.complianceId === node.complianceId);

    const builtNode: any = {
      id: uniqueId,
      themeID: newParentChain[0]?.id,
      originalId: node.id,
      label: node.name,
      recordId: isLeaf ? node.complianceId : undefined,
      clauseID: node.complianceId ? node.id : undefined,
      complianceId: node.complianceId,
      description: node.description,
      hasQuestions: isMatched,
      type: node.type,
      topicName: currentTopicName,
      policyName: newParentChain[0]?.name,
      children: [],
    };

    if (hasChildren) {
      const childResults = node.children
        .map((child: any) => traverse(child, uniqueId, currentTopicName, node.name, depth + 1, newParentChain))
        .flat(); // flatten arrays from merged nodes
      builtNode.children = childResults;
        if (depth === 0 && builtNode.children.length === 1 &&
          builtNode.children[0].children?.length > 0 &&
          !builtNode.children[0].originalId) {
        const onlyChild = builtNode.children[0];
        builtNode.label = onlyChild.label;
        builtNode.children = onlyChild.children;
      }
    }

    return builtNode;
  };

  return Array.isArray(data) ? data.map((theme: any) => traverse(theme, "", undefined, theme.name)).flat() : [];
};


function layoutTree(
  tree: any,
  expanded: Set<string>,
  selectedLeafId: string | null,
  depth = 0,
  startY = 0,
  onToggle?: (id: string) => void,
  onLeafClick?: (
    id: string,
    label: string,
    recordId?: number,
    clauseID?: number,
    topicName?: string,
    description?: string,
    themeID?: any,
    policyName?: any,
  ) => void
): { nodes: Node[]; edges: Edge[]; nextY: number; topY: number; bottomY: number } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  const x = depth * H_SPACING;
  const id = tree.id;

  const hasChildren = Array.isArray(tree.children) && tree.children.length > 0;
  const isExpanded = expanded.has(id) && hasChildren;
  const isLeaf = !hasChildren;

  const descendantHasMatch = (() => {
    const check = (n: any): boolean => {
      if (!n) return false;
      if (n.hasQuestions) return true;
      if (!n.children) return false;
      return n.children.some(check);
    };
    return check(tree);
  })();

const displayHasQuestions =
  Boolean(tree.hasQuestions) ||
  (!isExpanded && descendantHasMatch);


  if (!isExpanded) {
    const y = startY;
    nodes.push({
      id,
      type: "collapsible",
      position: { x, y },
      data: {
        id,
        themeID: tree.themeID,
        label: tree.label,
        recordId: tree.complianceId,
        clauseID: tree.clauseID,
        topicName: tree.topicName,
        policyName: tree.policyName,
        description: tree.description,
        isExpanded,
        onToggle: () => onToggle?.(id),
        onLeafClick,
        hasParent: depth > 0,
        hasChildren,
        isLeaf,
        isSelected: selectedLeafId === id,
        hasQuestions: displayHasQuestions,
      },
    });
    return {
      nodes,
      edges,
      nextY: startY + NODE_HEIGHT + V_SPACING,
      topY: y,
      bottomY: y + NODE_HEIGHT,
    };
  }

  let cursorY = startY;
  const childLayouts: any[] = [];

  for (const child of tree.children) {
    const childLayout = layoutTree(
      child,
      expanded,
      selectedLeafId,
      depth + 1,
      cursorY,
      onToggle,
      onLeafClick
    );
    nodes.push(...childLayout.nodes);
    edges.push(...childLayout.edges);

    edges.push({
      id: `${id}-->${child.id}`,
      source: id,
      target: child.id,
      type: "default",
      style: { stroke: "#0b1a4a", strokeDasharray: "4 4" },
      markerEnd: {
        type: MarkerType.ArrowClosed,
        width: 20,
        height: 20,
        color: "#0b1a4a",
      },
    });

    childLayouts.push(childLayout);
    cursorY = childLayout.nextY;
  }

  const subtreeTop = childLayouts[0].topY;
  const subtreeBottom = childLayouts[childLayouts.length - 1].bottomY;
  const childrenCenter = (subtreeTop + subtreeBottom) / 2;
  const parentTop = childrenCenter - NODE_HEIGHT / 2;

  nodes.push({
    id,
    type: "collapsible",
    position: { x, y: parentTop },
    data: {
      id,
      themeID: tree.themeID,
      label: tree.label,
      recordId: tree.recordId,
      clauseID: tree.clauseID,
      topicName: tree.topicName,
      policyName: tree.policyName,
      description: tree.description,
      isExpanded,
      onToggle: () => onToggle?.(id),
      onLeafClick,
      hasParent: depth > 0,
      hasChildren,
      isLeaf,
      isSelected: selectedLeafId === id,
      hasQuestions: Boolean(tree.hasQuestions),
    },
  });

  return {
    nodes,
    edges,
    nextY: cursorY,
    topY: Math.min(parentTop, subtreeTop),
    bottomY: Math.max(parentTop + NODE_HEIGHT, subtreeBottom),
  };
}
// ---------- Main component ----------
interface TreeProps {
  onLeafClick?: (id: string, label: string, recordId?: number, clauseID?: number, topicName?: string, description?: string, themeID?: any, policyName?: string) => void;
  questionData?: any[];
  showPreview?: boolean;
  selectQuestions?: any;
  renderButtonClass: () => void;
  setPreview?: (data: boolean) => void;
}

const TreeChart: React.FC<TreeProps> = ({ onLeafClick, questionData = [], showPreview, selectQuestions = [], setPreview, renderButtonClass }) => {
  const mockTrees = useMemo(() => buildTree(questionData, selectQuestions), [questionData, selectQuestions]);

  const initialExpanded = useMemo(() => {
    const expanded = new Set<string>();

    if (mockTrees.length > 0) {
      const firstTheme = mockTrees[0];
      if (firstTheme.children?.length > 0) {
        // expand theme and first policy only
        expanded.add(firstTheme.id);
      }
    }

    return expanded;
  }, [mockTrees]);

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(initialExpanded);
  const [selectedLeafId, setSelectedLeafId] = useState<string | null>(null);
  const handleToggle = useCallback(
    (nodeId: string) => {
      setExpandedNodes((prev) => {
        const newExpanded = new Set(prev);

        // Find the parent of the clicked node in the tree
        const findParent = (trees: any[], id: string): any => {
          for (const t of trees) {
            if (t.children?.some((c: any) => c.id === id)) return t;
            if (t.children) {
              const found = findParent(t.children, id);
              if (found) return found;
            }
          }
          return null;
        };

        const parent = findParent(mockTrees, nodeId);

        if (!parent) {
          // Root (Theme) clicked
          if (newExpanded.has(nodeId)) {
            // Already expanded → collapse
            newExpanded.delete(nodeId);
          } else {
            // Collapse other root nodes, expand only this one
            return new Set([nodeId]);
          }
          return newExpanded;
        }

        // Collapse siblings of clicked node
        parent.children.forEach((c: any) => {
          if (c.id !== nodeId) newExpanded.delete(c.id);
        });

        // Toggle clicked node
        if (newExpanded.has(nodeId)) {
          newExpanded.delete(nodeId); // collapse
        } else {
          newExpanded.add(nodeId); // expand
        }

        // Ensure parent is expanded
        newExpanded.add(parent.id);

        return newExpanded;
      });
    },
    [mockTrees]
  );

  const handleLeafClick = useCallback(
    (id: string, label: string, recordId?: number, clauseID?: number, topicName?: string, description?: string, themeID?: any, policyName?: any) => {
      setSelectedLeafId(id);
      onLeafClick?.(id, label, recordId, clauseID, topicName, description, themeID, policyName);
      // DO NOT collapse other nodes
    },
    [onLeafClick]
  );


  const trees = useMemo(() => mockTrees, [mockTrees]);

  const { nodes, edges } = useMemo(() => {
    const allNodes: Node[] = [];
    const allEdges: Edge[] = [];
    let cursorY = 40;

    for (const tree of trees) {
      const out = layoutTree(tree, expandedNodes, selectedLeafId, 0, cursorY, handleToggle, handleLeafClick);
      allNodes.push(...out.nodes);
      allEdges.push(...out.edges);
      cursorY = out.nextY + 40;
    }
    return { nodes: allNodes, edges: allEdges };
  }, [trees, expandedNodes, selectedLeafId, handleToggle, handleLeafClick]);

  return (
    <div style={{ width: "100%", height: "75vh", position: "relative" }}>
      <ReactFlowProvider>
        <ReactFlow
          nodes={nodes}
          proOptions={{ hideAttribution: true }}
          edges={edges}
          nodeTypes={nodeTypes}
          fitViewOptions={{ padding: 0.2 }}
          defaultViewport={{ x: 0, y: 0, zoom: 0 }}
          minZoom={0.6}
          panOnScroll
          zoomOnScroll
          nodesDraggable={false}
          nodesConnectable={false}
        />
        <div className="d-flex justify-content-end p-3">
          {selectQuestions.length > 0 && (
            <SubmitButton
              className={styles.previewBtn}
              onClick={() => setPreview?.(true)}
            >
              <TbEyeSearch size={22} /> {"Question Preview"}
            </SubmitButton>
          )}
        </div>
      </ReactFlowProvider>
    </div>
  );
};

export default TreeChart;
